import { inject, Injectable } from '@angular/core';
import { INews } from '@core/interfaces/news.interface';
import { NewsService } from '@core/services/news.service';
import { ComponentStore } from '@ngrx/component-store';
import { catchError, EMPTY, Observable, switchMap, tap } from 'rxjs';
import { matchesLanguageFilter } from './admin-news.util';

interface IAdminNewsState {
  news: INews[] | null;
  loading: boolean;
  error: string | null;
  // Id of the news entry currently being deleted, or null when no delete is in flight. Used to
  // disable deleting on every row while one is in progress, instead of switching the effect to
  // concatMap and having to reason about queued/concurrent deletes.
  deletingNewsId: string | null;
}

const initialState: IAdminNewsState = {
  deletingNewsId: null,
  error: null,
  loading: false,
  news: null,
};

@Injectable()
export class AdminNewsStore extends ComponentStore<IAdminNewsState> {
  public readonly news$ = this.selectSignal((state: IAdminNewsState) => state.news);
  public readonly loading$ = this.selectSignal((state: IAdminNewsState) => state.loading);
  public readonly error$ = this.selectSignal((state: IAdminNewsState) => state.error);
  public readonly deletingNewsId$ = this.selectSignal((state: IAdminNewsState) => state.deletingNewsId);

  public readonly fetchNewsAdmin = this.effect((languageCode$: Observable<string>) => {
    return languageCode$.pipe(
      tap(() => this.patchState({ error: null, loading: true })),
      switchMap((languageCode) =>
        this.newsService.getNewsAdmin(languageCode).pipe(
          tap((news: INews[]) => {
            this.patchState({ loading: false, news });
          }),
          catchError(() => this.handleError()),
        ),
      ),
    );
  });

  // Optimistic: removes the row immediately, re-fetches on failure so the table reflects reality
  // and the caller shows an error snackbar.
  public readonly deleteNews = this.effect((params$: Observable<{ id: string; languageCode: string }>) => {
    return params$.pipe(
      switchMap(({ id, languageCode }) => {
        const newsBeforeDelete = this.get().news ?? [];

        this.patchState({
          deletingNewsId: id,
          news: newsBeforeDelete.filter((news) => news.id !== id),
        });

        return this.newsService.deleteNews(id).pipe(
          tap(() => this.patchState({ deletingNewsId: null })),
          catchError(() => {
            this.patchState({ deletingNewsId: null });
            this.fetchNewsAdmin(languageCode);

            return EMPTY;
          }),
        );
      }),
    );
  });

  // A newly created item only belongs in the currently visible table if it matches the active
  // language filter - otherwise it would show up ahead of the next search, out of sync with what
  // "showing language X" is supposed to mean.
  public addNews(news: INews, currentLanguageCode: string): void {
    if (!matchesLanguageFilter(news, currentLanguageCode)) {
      return;
    }

    this.patchState({ news: [...(this.get().news ?? []), news] });
  }

  public replaceNews(news: INews, currentLanguageCode: string): void {
    const updatedNews = (this.get().news ?? []).flatMap((existing) => {
      if (existing.id !== news.id) {
        return [existing];
      }

      // Language may have changed on edit - drop it from view if it no longer matches the filter.
      return matchesLanguageFilter(news, currentLanguageCode) ? [news] : [];
    });

    this.patchState({ news: updatedNews });
  }

  private handleError(): Observable<never> {
    this.patchState({
      error: 'Failed to load news',
      loading: false,
    });

    return EMPTY;
  }

  private readonly newsService = inject(NewsService);

  constructor() {
    super(initialState);
  }
}
